import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, Field, TextInput } from "../components/Primitives";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const TABS = [
  { key: "organization", label: "Organization", icon: "Building2" },
  { key: "notifications", label: "Notifications & Email", icon: "Mail" },
];

export default function Settings() {
  const [tab, setTab] = useState("organization");
  return (
    <div>
      <PageHeader
        title="Settings"
        kicker="Organization details · notification email · SMTP delivery"
        description="Configure how your organization appears in reports and how the platform sends notifications."
        testId="settings-page-header"
      />
      <div className="flex gap-1 mb-4 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {TABS.map((t) => {
          const I = Icons[t.icon] || Icons.Circle;
          return (
            <button
              key={t.key}
              data-testid={`settings-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-[12px] font-medium rounded-md flex items-center gap-1.5 transition-colors"
              style={{
                background: tab === t.key ? "var(--primary-soft)" : "transparent",
                color: tab === t.key ? "var(--primary)" : "var(--muted)",
              }}
            >
              <I className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "organization" && <OrganizationTab />}
      {tab === "notifications" && <NotificationsTab />}
    </div>
  );
}

function OrganizationTab() {
  const [profile, setProfile] = useState(null);
  useEffect(() => { api.get("/org/profile").then((r) => setProfile(r.data)); }, []);
  const save = async () => {
    try { await api.put("/org/profile", profile); toast.success("Saved"); }
    catch { toast.error("Failed"); }
  };
  if (!profile) return <Loader />;
  return (
    <div className="surface rounded-xl p-6" style={{ border: "1px solid var(--border)" }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Organization name"><TextInput value={profile.name || ""} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></Field>
        <Field label="Industry"><TextInput value={profile.industry || ""} onChange={(e) => setProfile({ ...profile, industry: e.target.value })} /></Field>
        <Field label="Website"><TextInput value={profile.website || ""} onChange={(e) => setProfile({ ...profile, website: e.target.value })} /></Field>
        <Field label="Registered office"><TextInput value={profile.registered_office || ""} onChange={(e) => setProfile({ ...profile, registered_office: e.target.value })} /></Field>
        <Field label="DPO name"><TextInput value={profile.dpo_name || ""} onChange={(e) => setProfile({ ...profile, dpo_name: e.target.value })} /></Field>
        <Field label="DPO email"><TextInput value={profile.dpo_email || ""} onChange={(e) => setProfile({ ...profile, dpo_email: e.target.value })} /></Field>
        <Field label="DPO phone"><TextInput value={profile.dpo_phone || ""} onChange={(e) => setProfile({ ...profile, dpo_phone: e.target.value })} /></Field>
        <Field label="SDF designated">
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="settings-sdf" type="checkbox" checked={!!profile.sdf_designated} onChange={(e) => setProfile({ ...profile, sdf_designated: e.target.checked })} /> Designated as SDF
          </label>
        </Field>
      </div>
      <div className="mt-5 flex justify-end">
        <Btn data-testid="settings-save-btn" onClick={save}>Save changes</Btn>
      </div>
    </div>
  );
}

const PRESETS = [
  {
    id: "sendgrid",
    label: "SendGrid",
    icon: "Send",
    smtp_host: "smtp.sendgrid.net",
    smtp_port: 587,
    smtp_user: "apikey",
    smtp_starttls: true,
    hint: "Username is literally 'apikey'. Password is your SendGrid API key.",
  },
  {
    id: "ses",
    label: "Amazon SES",
    icon: "Cloud",
    smtp_host: "email-smtp.<region>.amazonaws.com",
    smtp_port: 587,
    smtp_user: "",
    smtp_starttls: true,
    hint: "Replace <region>. Use SMTP credentials (not IAM), verify the from-domain.",
  },
  {
    id: "resend",
    label: "Resend",
    icon: "Zap",
    smtp_host: "smtp.resend.com",
    smtp_port: 587,
    smtp_user: "resend",
    smtp_starttls: true,
    hint: "Username is 'resend'. Password is your Resend API key.",
  },
  {
    id: "google",
    label: "Google Workspace",
    icon: "Mail",
    smtp_host: "smtp.gmail.com",
    smtp_port: 587,
    smtp_user: "",
    smtp_starttls: true,
    hint: "Use a 16-character App Password (Google Account → Security → 2FA → App passwords).",
  },
  {
    id: "zoho",
    label: "Zoho Mail",
    icon: "AtSign",
    smtp_host: "smtp.zoho.in",
    smtp_port: 587,
    smtp_user: "",
    smtp_starttls: true,
    hint: "Use smtp.zoho.com for US accounts, smtp.zoho.in for India.",
  },
  {
    id: "custom",
    label: "Custom SMTP",
    icon: "Settings",
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_starttls: true,
    hint: "Enter your own SMTP server details.",
  },
];

function NotificationsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [preset, setPreset] = useState("");
  const [ccInput, setCcInput] = useState("");

  useEffect(() => {
    api.get("/settings/notifications").then((r) => setS(r.data)).catch(() => toast.error("Failed to load"));
  }, []);

  const applyPreset = (p) => {
    setPreset(p.id);
    setS({
      ...s,
      smtp_host: p.smtp_host,
      smtp_port: p.smtp_port,
      smtp_user: p.smtp_user || s.smtp_user,
      smtp_starttls: p.smtp_starttls,
    });
  };

  const addCc = () => {
    const v = ccInput.trim();
    if (!v) return;
    if ((s.cc_emails || []).includes(v)) { setCcInput(""); return; }
    setS({ ...s, cc_emails: [...(s.cc_emails || []), v] });
    setCcInput("");
  };
  const removeCc = (email) => setS({ ...s, cc_emails: (s.cc_emails || []).filter((c) => c !== email) });

  const save = async () => {
    setSaving(true);
    try {
      // If password is still masked, send empty string so backend keeps existing
      const payload = { ...s };
      if ((payload.smtp_password || "").match(/^•+$/)) payload.smtp_password = "";
      const r = await api.put("/settings/notifications", payload);
      setS(r.data);
      toast.success("Notification settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a test email address"); return; }
    setTesting(true);
    try {
      // Send unsaved-in-flight settings so user can test before saving
      const override = { ...s };
      if ((override.smtp_password || "").match(/^•+$/)) override.smtp_password = "";
      const r = await api.post("/settings/notifications/test", { to: testTo.trim(), override });
      if (r.data?.ok === false) {
        toast.error(r.data.error || "Test delivery failed");
      } else {
        toast.success(`Test email delivered to ${r.data.delivered_to}`);
      }
      // Refresh to show last_test_status
      const g = await api.get("/settings/notifications");
      setS(g.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed — check logs");
    } finally {
      setTesting(false);
    }
  };

  if (!s) return <Loader />;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-4">
        {/* Enable toggle */}
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }} data-testid="notif-enable-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold ink flex items-center gap-2">
                <Icons.MailCheck className="w-4 h-4" style={{ color: "var(--primary)" }} />
                Email notifications
              </div>
              <div className="text-[11.5px] muted mt-1 leading-relaxed">
                When enabled, the platform sends emails on key events — consent withdrawal, DSAR requests, breach notifications and any in-app alert (using the from-address below).
              </div>
            </div>
            <Toggle checked={s.emails_enabled} onChange={(v) => setS({ ...s, emails_enabled: v })} testId="notif-enabled-toggle" />
          </div>
          {s.last_test_status && (
            <div className="mt-3 p-2 rounded-md text-[11px] flex items-start gap-2"
              style={{ background: s.last_test_status === "ok" ? "var(--success-soft)" : "var(--danger-soft)",
                       color: s.last_test_status === "ok" ? "var(--success)" : "var(--danger)" }}>
              {s.last_test_status === "ok"
                ? <Icons.CheckCircle2 className="w-3.5 h-3.5 mt-[1px]" />
                : <Icons.AlertCircle className="w-3.5 h-3.5 mt-[1px]" />}
              <div>
                <div className="font-semibold">
                  Last test: {s.last_test_status === "ok" ? "delivered" : "failed"} · {new Date(s.last_tested_at).toLocaleString()}
                </div>
                {s.last_test_error && <div className="mt-0.5 opacity-90">{s.last_test_error}</div>}
              </div>
            </div>
          )}
        </div>

        {/* Provider presets */}
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }} data-testid="notif-preset-card">
          <div className="text-[13px] font-semibold ink mb-1">SMTP provider</div>
          <div className="text-[11.5px] muted mb-3">Pick a preset to auto-fill host & port, then enter your credentials below.</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PRESETS.map((p) => {
              const I = Icons[p.icon] || Icons.Mail;
              const active = preset === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`notif-preset-${p.id}`}
                  onClick={() => applyPreset(p)}
                  className="p-2.5 rounded-lg text-left transition-all"
                  style={{
                    background: active ? "var(--primary-soft)" : "var(--bg-2)",
                    border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
                  }}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <I className="w-3.5 h-3.5" style={{ color: active ? "var(--primary)" : "var(--muted)" }} />
                    <div className="text-[12px] font-semibold" style={{ color: active ? "var(--primary)" : "var(--ink)" }}>{p.label}</div>
                  </div>
                  <div className="text-[10.5px] muted leading-snug">{p.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* SMTP credentials */}
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }} data-testid="notif-smtp-card">
          <div className="text-[13px] font-semibold ink mb-3">SMTP credentials</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="SMTP host">
              <TextInput data-testid="notif-smtp-host" value={s.smtp_host || ""} placeholder="smtp.sendgrid.net"
                onChange={(e) => setS({ ...s, smtp_host: e.target.value })} />
            </Field>
            <Field label="Port">
              <TextInput data-testid="notif-smtp-port" type="number" value={s.smtp_port || 587}
                onChange={(e) => setS({ ...s, smtp_port: parseInt(e.target.value || 587, 10) })} />
            </Field>
            <Field label="Username">
              <TextInput data-testid="notif-smtp-user" value={s.smtp_user || ""} placeholder="apikey / user@yourdomain.com"
                onChange={(e) => setS({ ...s, smtp_user: e.target.value })} />
            </Field>
            <Field label="Password / API key">
              <TextInput data-testid="notif-smtp-password" type="password" value={s.smtp_password || ""}
                placeholder="Leave unchanged to keep existing"
                onChange={(e) => setS({ ...s, smtp_password: e.target.value })} />
            </Field>
            <Field label="Use STARTTLS">
              <label className="flex items-center gap-2 text-[12px] mt-1">
                <input type="checkbox" data-testid="notif-starttls" checked={!!s.smtp_starttls}
                  onChange={(e) => setS({ ...s, smtp_starttls: e.target.checked })} />
                Encrypt with STARTTLS on connect (recommended)
              </label>
            </Field>
          </div>
        </div>

        {/* From / Reply-to / CC */}
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }} data-testid="notif-from-card">
          <div className="text-[13px] font-semibold ink mb-1">Sender identity</div>
          <div className="text-[11.5px] muted mb-3">The single from-address used on every notification the platform sends.</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="From email *">
              <TextInput data-testid="notif-from-email" value={s.from_email || ""} placeholder="notifications@dpdpcrm.com"
                onChange={(e) => setS({ ...s, from_email: e.target.value })} />
            </Field>
            <Field label="From name">
              <TextInput data-testid="notif-from-name" value={s.from_name || ""} placeholder="DPDP Compliance"
                onChange={(e) => setS({ ...s, from_name: e.target.value })} />
            </Field>
            <Field label="Reply-to (optional)">
              <TextInput data-testid="notif-reply-to" value={s.reply_to || ""} placeholder="dpo@dpdpcrm.com"
                onChange={(e) => setS({ ...s, reply_to: e.target.value })} />
            </Field>
            <Field label="CC on every notification (optional)">
              <div className="flex gap-1">
                <TextInput data-testid="notif-cc-input" value={ccInput} placeholder="dpo@dpdpcrm.com"
                  onChange={(e) => setCcInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }} />
                <Btn variant="secondary" onClick={addCc} data-testid="notif-cc-add">Add</Btn>
              </div>
              {(s.cc_emails || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {s.cc_emails.map((e) => (
                    <span key={e} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px]"
                      style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
                      {e}
                      <button onClick={() => removeCc(e)} className="hover:opacity-70"><Icons.X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </Field>
          </div>
        </div>

        {/* Which events */}
        <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }} data-testid="notif-events-card">
          <div className="text-[13px] font-semibold ink mb-3">Send an email when…</div>
          <div className="space-y-2">
            {[
              { key: "notify_on_consent_withdraw", label: "Data Principal withdraws a consent", desc: "Confirmation email to the principal + CC list" },
              { key: "notify_on_dsar_received",   label: "DSAR / Rights request is received",   desc: "Alert to DPO/CC list" },
              { key: "notify_on_breach",           label: "Personal data breach is logged",       desc: "Alert to DPO/CC list" },
            ].map((ev) => (
              <label key={ev.key} className="flex items-start gap-2.5 p-2 rounded-md hover:bg-app-2 cursor-pointer">
                <input type="checkbox" checked={!!s[ev.key]} data-testid={`notif-event-${ev.key}`}
                  onChange={(e) => setS({ ...s, [ev.key]: e.target.checked })} className="mt-0.5" />
                <div>
                  <div className="text-[12.5px] font-medium ink">{ev.label}</div>
                  <div className="text-[11px] muted">{ev.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Btn data-testid="notif-save-btn" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save notification settings"}
          </Btn>
        </div>
      </div>

      {/* Test panel */}
      <div className="lg:col-span-1">
        <div className="surface rounded-xl p-5 sticky top-4" style={{ border: "1px solid var(--border)" }} data-testid="notif-test-card">
          <div className="flex items-center gap-2 mb-1">
            <Icons.Send className="w-4 h-4" style={{ color: "var(--primary)" }} />
            <div className="text-[13px] font-semibold ink">Send test email</div>
          </div>
          <div className="text-[11.5px] muted mb-3 leading-relaxed">
            Uses the values above (no need to save first). Delivers to the address you enter and marks the panel with the result.
          </div>
          <Field label="Send test to">
            <TextInput data-testid="notif-test-to" value={testTo} placeholder="you@example.com"
              onChange={(e) => setTestTo(e.target.value)} />
          </Field>
          <Btn data-testid="notif-test-send" onClick={sendTest} disabled={testing} className="w-full mt-2">
            {testing ? "Sending…" : "Send test email"}
          </Btn>

          <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
            <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-2">Where these are used</div>
            <div className="space-y-1.5">
              {[
                ["MailCheck", "Consent withdrawal confirmations to Data Principals"],
                ["Users",     "DSAR / Rights request alerts to your team"],
                ["ShieldAlert","Personal data breach alerts"],
                ["Bell",      "In-app notifications (opt-in fan-out)"],
              ].map(([ic, label]) => {
                const I = Icons[ic] || Icons.Circle;
                return (
                  <div key={label} className="flex items-center gap-2 text-[11px] muted">
                    <I className="w-3 h-3" style={{ color: "var(--primary)" }} />
                    {label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 pt-4 text-[10.5px] muted leading-relaxed" style={{ borderTop: "1px solid var(--border)" }}>
            <Icons.Lock className="w-3 h-3 inline mr-1" />
            Passwords are encrypted at rest and never returned to the browser.
          </div>
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      className="relative w-11 h-6 rounded-full transition-colors"
      style={{ background: checked ? "var(--primary)" : "var(--border)" }}
    >
      <span
        className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
        style={{ transform: `translateX(${checked ? 22 : 2}px)` }}
      />
    </button>
  );
}
